import { isNotFound, Link, useRouteContext } from '@tanstack/react-router';
import { sdgGoalUrl } from '@/lib/sdg-utils';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { Card, CardContent, CardFooter } from '@worldcoo/ui/components/card';
import { CampaignStatistics } from './statistics-campaign';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { Button, buttonVariants } from '@worldcoo/ui/components/button';
import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { type ReactNode, Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { RingProgressBar } from './ring-progress-bar';
import { Separator } from '@worldcoo/ui/components/separator';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { Image } from '../../-components/image';
import { SdgIcon } from '@/lib/sdg-icons';
import {
	usePortalAuthorQuery,
	usePortalCampaignQuery,
	usePortalCampaignsAggregatesQuery,
	usePortalMetadataQuery,
} from '@/lib/data-hooks';
import { PortalAnimateNumber } from './portal-animate-number';
import { useCampaignDisplayContext } from './campaign-display-context';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { sdgMessages } from '@/lib/portal-messages';

export function CampaignDetails(props: { campaignId: string }) {
	const { t } = useLingui();
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		const { campaign, locale, aggregates, onDonate } = displayData;
		return (
			<CampaignDetailsLayout
				progress={
					aggregates &&
					aggregates.funding != null && (
						<>
							<div className="flex items-end gap-5">
								<RingProgressBar
									radius={40}
									stroke={6}
									progress={Math.min(Math.ceil(aggregates.progress), 100)}
									strokeColor="var(--primary)"
									color="var(--primary)"
								/>
								<div className="flex flex-1 flex-col">
									<span className="inline-flex text-[32px] leading-[100%]">{Math.ceil(aggregates.progress)}%</span>
									<span className="text-primary text-xs font-semibold lg:text-base">{t`Funded`}</span>
								</div>
							</div>
							<Separator />
						</>
					)
				}
				statistics={
					aggregates && (
						<div className={cn('bg-background flex gap-16', displayData.metadata.hideCollected && 'justify-center')}>
							<div className="flex flex-col font-medium">
								<DonationsIcon className="text-primary mb-2 size-6" />
								<div className="text-xl whitespace-nowrap">{formatNumber(locale, aggregates.donors, 0)}</div>
								<span className="text-primary text-sm font-semibold text-nowrap">{t`Donations`}</span>
							</div>
							{!displayData.metadata.hideCollected && (
								<div className="flex flex-col font-medium">
									<CollectedIcon className="text-primary mb-2 size-6" />
									<div className="text-xl whitespace-nowrap">{formatNumber(locale, aggregates.collected, 2)}€</div>
									<span className="text-primary text-sm font-semibold text-nowrap">
										{aggregates.funding
											? (() => {
													const goal = formatNumber(locale, aggregates.funding, 0);
													return t`Goal of ${goal}` + ' €';
												})()
											: t`Collected`}
									</span>
								</div>
							)}
						</div>
					)
				}
				donateButton={
					campaign.finished ? (
						<Button size="xl" className="mt-auto w-full" disabled>
							{t`Campaign funded`}
						</Button>
					) : (
						<Button size="xl" className="mt-auto w-full" onClick={onDonate}>
							{t`Make donation`}
						</Button>
					)
				}
				details={
					<>
						<BeneficiariesCard beneficiaries={campaign.beneficiaries} locale={locale} />
						<SdgGoalsCard sdgs={campaign.sdgs} locale={locale} />
					</>
				}
			/>
		);
	}
	return (
		<Suspense>
			<SuspensedCampaignDetails campaignId={props.campaignId} />
		</Suspense>
	);
}

function SuspensedCampaignDetails(props: { campaignId: string }) {
	return (
		<CampaignDetailsLayout
			progress={
				<Suspense fallback={<Skeleton />}>
					<CampaignProgress campaignId={props.campaignId} />
				</Suspense>
			}
			statistics={<CampaignStatistics campaignId={props.campaignId} variant="sm" textEffectDisabled />}
			donateButton={<DonateButton campaignId={props.campaignId} />}
			details={<CampaignNgos campaignId={props.campaignId} />}
		/>
	);
}

function CampaignDetailsLayout(props: {
	progress: ReactNode;
	statistics: ReactNode;
	donateButton: ReactNode;
	details: ReactNode;
}) {
	return (
		<aside className="flex flex-col gap-6">
			<DivWithPrimaryColorVariable>
				<Card className="shadow-4xl border-none">
					<CardContent className="flex flex-col gap-4">
						{props.progress}
						{props.statistics}
					</CardContent>
					<CardFooter className="hidden lg:flex">{props.donateButton}</CardFooter>
				</Card>
			</DivWithPrimaryColorVariable>

			{props.details}
		</aside>
	);
}

function CampaignProgress(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const metadata = usePortalMetadataQuery();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	const funding = campaignAggregate?.funding;
	const progressValue = campaignAggregate?.progress ?? 0;
	const progress = useDebounce(
		((!('isPageLoaded' in campaign) || !campaign.isPageLoaded) && !isClient) || funding == null
			? 0
			: Math.ceil(progressValue),
		150,
	);

	if (funding == null || isNotFound(campaign) || isNotFound(metadata)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !campaignAggregate?.finished) {
		return null;
	}

	return (
		<>
			<div className="flex items-end gap-5">
				<RingProgressBar
					radius={40}
					stroke={6}
					progress={Math.min(progress, 100)}
					strokeColor="var(--primary)"
					color="var(--primary)"
				/>
				<div className="flex flex-1 flex-col">
					<span className="inline-flex text-[32px] leading-[100%]">
						<PortalAnimateNumber locale={language} value={progress} suffix="%" />
					</span>
					<span className="text-primary text-xs font-semibold lg:text-base">{t`Funded`}</span>
				</div>
			</div>

			<Separator />
		</>
	);
}

function DonateButton(props: { campaignId: string }) {
	const { t } = useLingui();
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (!portalId || isNotFound(campaign)) return null;

	return campaign.data.finished || campaign.data.disabled ? (
		<Button size="xl" className="mt-auto w-full" disabled>
			{t`Campaign funded`}
		</Button>
	) : (
		<Link
			to="/$portalId/$campaignId/donate"
			params={{ portalId, campaignId: campaign.data.slugsByLocale[locale] ?? campaign.data.id }}
			search={(prev) => prev}
			className={cn(buttonVariants({ size: 'xl' }), 'mt-auto w-full')}
		>
			{t`Make donation`}
		</Link>
	);
}

function useIsSingleNgoPortal(ngos: Array<{ id: string }>) {
	const author = usePortalAuthorQuery();
	if (isNotFound(author)) return false;
	return author.type === 'ngo' && ngos.length === 1 && ngos[0]?.id === author.ngoId;
}

function CampaignNgos(props: { campaignId: string }) {
	const { t } = useLingui();
	const campaign = usePortalCampaignQuery(props.campaignId);
	const isSingleNgo = useIsSingleNgoPortal(isNotFound(campaign) ? [] : campaign.data.ngos);

	if (isNotFound(campaign)) return null;

	return (
		<>
			<DivWithPrimaryColorVariable className="flex flex-col gap-3">
				<Card className="shadow-4xl border-none">
					<CardContent className="flex-gap flex flex-col gap-4">
						<Suspense fallback={<Skeleton />}>
							<Beneficiaries campaignId={props.campaignId} />
						</Suspense>

						{!isSingleNgo && (
							<>
								<Separator className="first:hidden" />

								{t`A campaign from: `}
								<ul className="flex flex-wrap justify-evenly">
									{campaign.data.ngos
										// There is not a wide support for toSorted() in the browser
										// oxlint-disable-next-line unicorn/no-array-sort
										.sort((a, b) => a.name?.localeCompare(b.name ?? '') ?? 0)
										.filter((ngo) => ngo.logo !== undefined)
										.map((ngo) => (
											<li key={ngo.id} className="m-auto p-2">
												<Image
													className="m-auto object-contain! p-2"
													src={ngo.logo!.location}
													objectFit="contain"
													height={96}
													aspectRatio={ngo.logo!.width / ngo.logo!.height}
													alt={ngo.name ?? ''}
												/>
											</li>
										))}
								</ul>
							</>
						)}
					</CardContent>
				</Card>
			</DivWithPrimaryColorVariable>

			<SdgGoals campaignId={props.campaignId} />

			{campaign.data.ngos.some((ngo) => ngo.accreditedByLealtad) && (
				<Image
					src="/accredited-by-lealtad.png"
					height={57}
					aspectRatio={128 / 57}
					// TODO: translate for accessibility
					alt="accredited by lealtad"
				/>
			)}
		</>
	);
}

function Beneficiaries(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return;
	if (!campaign.data.beneficiaries.direct || !campaign.data.beneficiaries.indirect) return;

	return (
		<>
			{t`Beneficiaries`}

			<div className="flex items-center gap-6">
				<div className="flex items-center gap-2">
					<span className="font-[Helvetica] text-xl tracking-tight">
						{formatNumber(language, campaign.data.beneficiaries.direct, 0)}
					</span>
					<span className="text-primary text-sm font-medium">{t`direct`}</span>
				</div>

				<div className="flex items-center gap-2">
					<span className="font-[Helvetica] text-xl tracking-tight">
						{formatNumber(language, campaign.data.beneficiaries.indirect, 0)}
					</span>
					<span className="text-primary text-sm font-medium">{t`indirect`}</span>
				</div>
			</div>
		</>
	);
}

function SdgGoals(props: { campaignId: string }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	return <SdgGoalsCard sdgs={campaign.data.sdgs} locale={locale} />;
}

function BeneficiariesCard(props: { beneficiaries: { direct: number; indirect: number }; locale: string }) {
	const { t } = useLingui();
	const hasBeneficiaries = props.beneficiaries.direct > 0 || props.beneficiaries.indirect > 0;
	if (!hasBeneficiaries) return null;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col gap-3">
			<Card className="shadow-4xl border-none">
				<CardContent className="flex flex-col gap-4">
					{t`Beneficiaries`}

					<div className="flex items-center gap-6">
						{props.beneficiaries.direct > 0 && (
							<div className="flex items-center gap-2">
								<span className="font-[Helvetica] text-xl tracking-tight">
									{formatNumber(props.locale, props.beneficiaries.direct, 0)}
								</span>
								<span className="text-primary text-sm font-medium">{t`direct`}</span>
							</div>
						)}

						{props.beneficiaries.indirect > 0 && (
							<div className="flex items-center gap-2">
								<span className="font-[Helvetica] text-xl tracking-tight">
									{formatNumber(props.locale, props.beneficiaries.indirect, 0)}
								</span>
								<span className="text-primary text-sm font-medium">{t`indirect`}</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</DivWithPrimaryColorVariable>
	);
}

function SdgGoalsCard(props: { sdgs?: number[]; locale: string }) {
	const { t } = useLingui();
	if (!props.sdgs || props.sdgs.length === 0) return null;

	const title = t`Sustainable Development Goals`;

	return (
		<DivWithPrimaryColorVariable className="flex flex-col gap-3">
			<Card className="shadow-4xl border-none">
				<CardContent className="flex flex-col gap-3">
					<span className="text-sm font-medium">{title}</span>
					<ul className="flex flex-col gap-2" aria-label={title}>
						{props.sdgs.map((sdgNumber) => {
							const name = sdgMessages[sdgNumber] ? t(sdgMessages[sdgNumber]) : '';
							return (
								<li key={sdgNumber}>
									<a
										href={sdgGoalUrl(sdgNumber, props.locale)}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-2 rounded transition-opacity hover:opacity-80"
										aria-label={`${name} — SDG ${sdgNumber}`}
									>
										<SdgIcon goal={sdgNumber} className="size-10 shrink-0 rounded" />
										<span className="text-sm">{name}</span>
									</a>
								</li>
							);
						})}
					</ul>
				</CardContent>
			</Card>
		</DivWithPrimaryColorVariable>
	);
}
